// Renders one analysis card per locked-on person and keeps the set in sync with
// who is currently watching. Cards for people who look away are removed.

import type { PersonAnalysis, TrackedFace } from "../types";

export class Cards {
  private els = new Map<number, HTMLElement>();

  constructor(private container: HTMLElement) {}

  sync(watchers: TrackedFace[]): void {
    const live = new Set(watchers.map((w) => w.id));

    // Remove cards for people no longer watching.
    for (const [id, el] of this.els) {
      if (!live.has(id)) {
        el.remove();
        this.els.delete(id);
      }
    }

    for (const w of watchers) {
      let el = this.els.get(w.id);
      if (!el) {
        el = document.createElement("article");
        el.className = "card";
        this.container.appendChild(el);
        this.els.set(w.id, el);
      }
      el.innerHTML = this.body(w);
    }
  }

  private body(w: TrackedFace): string {
    const dist = w.distanceM > 0 ? `≈${w.distanceM.toFixed(1)} m` : "—";
    const head = `
      <div class="card-head">
        <span class="card-id">#${w.id}</span>
        <span class="card-dist">${dist}</span>
      </div>`;

    switch (w.analysis.status) {
      case "pending":
        return head + `<div class="card-state">analysing…</div>`;
      case "error":
        return head + `<div class="card-state card-err">analysis failed</div>`;
      case "none":
        return head + `<div class="card-state">locking…</div>`;
      case "done":
        return head + this.analysisRows(w.analysis.data);
    }
  }

  private analysisRows(a: PersonAnalysis): string {
    const row = (label: string, value: string | null) =>
      `<div class="card-row"><span>${label}</span><b>${value ?? "—"}</b></div>`;
    const attention =
      a.attentionScore != null ? `${Math.round(a.attentionScore * 100)}%` : null;

    const cycle = a.cyclePhase;
    const cycleRow = cycle
      ? `<div class="card-row card-cycle" title="${escapeAttr(cycle.disclaimer)}">
           <span>cycle*</span><b>${cycle.value ?? "not detectable"}</b>
         </div>
         <div class="card-note">*${escapeHtml(cycle.disclaimer)}</div>`
      : "";

    return (
      row("sex", a.sex) +
      row("age", a.ageBand) +
      row("expression", a.expression) +
      row("attention", attention) +
      cycleRow
    );
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] ?? c);
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;");
}
